import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RadioContext = createContext();

export const useRadio = () => {
  const context = useContext(RadioContext);
  if (!context) {
    throw new Error("useRadio must be used within a RadioProvider");
  }
  return context;
};

export const RadioProvider = ({ children }) => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [radioEnabled, setRadioEnabled] = useState(() => {
    // Check if user has enabled radio before
    return localStorage.getItem("e1_radio_enabled") === "true";
  });
  const [needsUserInteraction, setNeedsUserInteraction] = useState(false);
  const audioRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  const fetchRandomTrack = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/radio/random`);
      return response.data;
    } catch (error) {
      console.error("Error fetching random track:", error);
      return null;
    }
  }, []);

  const playTrack = useCallback((track) => {
    // Stop current audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current = null;
    }

    // Build full URL for MP3
    const mp3Url = track.mp3_url.startsWith('http') 
      ? track.mp3_url 
      : `${process.env.REACT_APP_BACKEND_URL}${track.mp3_url}`;
    
    const newAudio = new Audio(mp3Url);
    audioRef.current = newAudio;
    
    // When track ends, play next one automatically (24/7 radio)
    newAudio.onended = async () => {
      const nextTrack = await fetchRandomTrack();
      if (nextTrack) {
        playTrack(nextTrack);
      } else {
        // If failed to get next track, retry after 5 seconds
        retryTimeoutRef.current = setTimeout(async () => {
          const retryTrack = await fetchRandomTrack();
          if (retryTrack) {
            playTrack(retryTrack);
          }
        }, 5000);
      }
    };

    // Handle errors - try to recover
    newAudio.onerror = async () => {
      console.log("Audio error, trying next track...");
      const nextTrack = await fetchRandomTrack();
      if (nextTrack) {
        playTrack(nextTrack);
      }
    };
    
    newAudio.play()
      .then(() => {
        setIsPlaying(true);
        setNeedsUserInteraction(false);
        // Save that radio is enabled
        localStorage.setItem("e1_radio_enabled", "true");
        setRadioEnabled(true);
      })
      .catch((error) => {
        console.log("Autoplay blocked by browser:", error.message);
        setNeedsUserInteraction(true);
        setIsPlaying(false);
      });
    
    setCurrentTrack(track);
  }, [fetchRandomTrack]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Auto-play radio when app opens (24/7 behavior)
  useEffect(() => {
    const startRadio = async () => {
      const track = await fetchRandomTrack();
      if (track) {
        playTrack(track);
      }
    };
    
    // Always try to start radio
    startRadio();
  }, [fetchRandomTrack, playTrack]);

  // Listen for user interaction to enable audio
  useEffect(() => {
    if (!needsUserInteraction) return;

    const enableAudioOnInteraction = async () => {
      if (audioRef.current && !isPlaying) {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
          setNeedsUserInteraction(false);
          localStorage.setItem("e1_radio_enabled", "true");
        } catch (e) {
          // Still blocked, user needs to click the play button
        }
      }
    };

    // Try to play on any user interaction
    document.addEventListener('click', enableAudioOnInteraction, { once: true });
    document.addEventListener('touchstart', enableAudioOnInteraction, { once: true });

    return () => {
      document.removeEventListener('click', enableAudioOnInteraction);
      document.removeEventListener('touchstart', enableAudioOnInteraction);
    };
  }, [needsUserInteraction, isPlaying]);

  const togglePlay = async () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    if (audioRef.current && currentTrack) {
      // Resume current track
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        setNeedsUserInteraction(false);
        localStorage.setItem("e1_radio_enabled", "true");
      } catch (e) {
        console.log("Play blocked");
        setNeedsUserInteraction(true);
      }
    } else {
      // Fetch and play new track
      const track = await fetchRandomTrack();
      if (track) {
        playTrack(track);
      }
    }
  };

  const skipTrack = async () => {
    const track = await fetchRandomTrack();
    if (track) {
      playTrack(track);
    }
  };

  // Force enable radio (called when user explicitly clicks play)
  const enableRadio = async () => {
    const track = await fetchRandomTrack();
    if (track) {
      playTrack(track);
    }
  };

  return (
    <RadioContext.Provider value={{ 
      currentTrack, 
      isPlaying, 
      togglePlay,
      skipTrack,
      enableRadio,
      needsUserInteraction,
      radioEnabled
    }}>
      {children}
    </RadioContext.Provider>
  );
};
